/**
 * Position identity for lesson lookup.
 * Key = piece placement + side to move + castling rights + meaningful en passant.
 * Halfmove and fullmove clocks are ignored so transpositions match.
 */

export type FenParts = {
  placement: string;
  side: string;
  castling: string;
  ep: string;
};

export function parseFen(fen: string): FenParts {
  const parts = fen.trim().split(/\s+/);
  if (parts.length < 4) {
    throw new Error(`Invalid FEN (need at least 4 fields): ${fen}`);
  }
  return {
    placement: parts[0],
    side: parts[1],
    castling: normalizeCastling(parts[2]),
    ep: parts[3],
  };
}

export function normalizeCastling(raw: string): string {
  if (!raw || raw === "-") return "-";
  const order = ["K", "Q", "k", "q"] as const;
  const present = new Set(raw.split(""));
  const out = order.filter((flag) => present.has(flag)).join("");
  return out || "-";
}

function expandRank(rank: string): (string | null)[] {
  const squares: (string | null)[] = [];
  for (const ch of rank) {
    if (ch >= "1" && ch <= "8") {
      const n = Number(ch);
      for (let i = 0; i < n; i++) squares.push(null);
    } else {
      squares.push(ch);
    }
  }
  return squares;
}

function boardFromPlacement(placement: string): (string | null)[][] {
  return placement.split("/").map(expandRank);
}

function fileIndex(file: string): number {
  return file.charCodeAt(0) - 97;
}

/**
 * En passant is only part of the key when a pawn can actually capture on that square.
 */
export function enPassantIsMeaningful(placement: string, side: string, ep: string): boolean {
  if (!ep || ep === "-") return false;
  if (!/^[a-h][36]$/.test(ep)) return false;
  const file = fileIndex(ep[0]);
  const board = boardFromPlacement(placement);
  if (board.some((rank) => rank.length !== 8) || board.length !== 8) return false;

  if (side === "w" && ep[1] === "6") {
    const pawnRank = board[3]; // rank 5
    for (const delta of [-1, 1]) {
      const f = file + delta;
      if (f >= 0 && f <= 7 && pawnRank[f] === "P") return true;
    }
  }
  if (side === "b" && ep[1] === "3") {
    const pawnRank = board[4]; // rank 4
    for (const delta of [-1, 1]) {
      const f = file + delta;
      if (f >= 0 && f <= 7 && pawnRank[f] === "p") return true;
    }
  }
  return false;
}

export function positionKey(fen: string): string {
  const { placement, side, castling, ep } = parseFen(fen);
  const epKey = enPassantIsMeaningful(placement, side, ep) ? ep : "-";
  return `${placement} ${side} ${castling} ${epKey}`;
}

export function samePosition(fenA: string, fenB: string): boolean {
  return positionKey(fenA) === positionKey(fenB);
}

export const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
